import {BackendContext} from "../../../../context/BackendContext"
import {useGitDir} from "../../../../hooks/useGitDir"
import {GitTree} from "../../../../interfaces/repos"
import {useParams} from "react-router-dom"
import {useCallback, useContext, useEffect, useState} from "react"
import git, {ReadCommitResult, WalkerEntry} from "isomorphic-git"
import {getTimeFromNow} from "../../../../functions/time"
import SyntaxHighlighter from "react-syntax-highlighter"
import {github} from "react-syntax-highlighter/dist/esm/styles/hljs"
import FS from "@isomorphic-git/lightning-fs"
import styles from "./index.module.scss"


interface BlobProps {
  gitTree: GitTree
  cloneCnt: number
}

export const Blob = (props: BlobProps) => {
  const {orbitId, repoId, branch} = useParams()
  const params = useParams()

  const gitDir = useGitDir(orbitId, repoId)
  const {fs} = useContext(BackendContext)
  const [isBlobValid, setIsBlobValid] = useState<boolean>()
  const [blobContent, setBlobContent] = useState<string>("")
  const [blobLines, setBlobLines] = useState<number>(-1)
  const [lastCommit, setLastCommit] = useState<ReadCommitResult>()
  const [fileExt, setFileExt] = useState<string>()

  const getBlobContent = useCallback(async (
    fs: FS,
    gitDir: string,
    branch: string,
    filePath: string,
    walkerEntry: WalkerEntry
  ) => {
    const commits = await git.log({
      fs,
      gitdir: gitDir,
      ref: branch,
      filepath: filePath,
      force: true,
      depth: 1
    })
    if (commits.length === 0) {
      return
    }
    setLastCommit(commits[0])

    const fileExt = filePath.split(".").pop()?.toLowerCase()
    setFileExt(fileExt)

    const content = await walkerEntry.content()
    if (content == null) {
      setIsBlobValid(false)
      return
    }

    const decoded = new TextDecoder("utf-8").decode(content)
    setBlobLines((decoded.match(/\n/g) || "").length)
    setBlobContent(decoded)
    setIsBlobValid(true)
  }, [])

  useEffect(() => {
    const blobName = params["*"]
    const gitTree = props.gitTree
    if (!blobName || !fs || !gitDir || !gitTree || !branch) {
      return
    }

    const walkerEntry = gitTree[blobName]
    if (!walkerEntry) {
      return
    }

    getBlobContent(fs, gitDir, branch, blobName, walkerEntry).then()
  }, [getBlobContent, fs, gitDir, props.gitTree, params, branch])

  return (
    <div className={styles.Container}>
      <div className={styles.HeaderRow}>
        {
          lastCommit && (
            <div className={styles.CommitInfo}>
              <div className={styles.LastCommitMessage}>
                {lastCommit.commit.author.name} - {lastCommit.commit.message}
              </div>

              <div className={styles.LastCommitHash}>
                Last commit: {lastCommit.oid.slice(0, 7)}
              </div>

              <div className={styles.LastCommitTime}>
                {getTimeFromNow(lastCommit.commit.author.timestamp)}
              </div>
            </div>
          )
        }
      </div>

      {
        blobLines >= 0 && (
          <div className={styles.BlobInfoRow}>
            {blobLines} line{blobLines === 1 ? "" : "s"}
          </div>
        )
      }

      {
        isBlobValid && blobLines > 0 && (
          <SyntaxHighlighter
            language={fileExt ?? undefined}
            showLineNumbers={true}
            style={github}
            className={styles.BlobContent}
          >
            {blobContent.trim()}
          </SyntaxHighlighter>
        )
      }
    </div>
  )
}
